import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GooglePlacesProxyInput = ({ 
  value, 
  onChange, 
  onLocationNameChange,
  onCoordinatesChange,
  placeholder = "Enter address...",
  className = "jop-input",
  label = "Address"
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionToken] = useState(() => `session_${Date.now()}_${Math.random()}`);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  const searchPlaces = async (query) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: {
          q: query,
          session: sessionToken
        }
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        setSuggestions(response.data.predictions);
        setShowSuggestions(true);
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
        setShowSuggestions(false);
      } else {
        console.error('Places API status:', response.data.status);
        setError('No addresses found');
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Places API error:', error);
      
      if (error.response?.status === 429) {
        setError('Too many requests - please wait a moment');
        toast.error('Too many address searches - please wait a moment');
      } else if (error.response?.status === 500) {
        setError('Address lookup temporarily unavailable');
      } else {
        setError('Address lookup failed');
      }
      
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce API calls (300ms as recommended)
    if (inputValue.length >= 3) {
      timeoutRef.current = setTimeout(() => {
        searchPlaces(inputValue);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setError(null);
    }
  };

  const handleSuggestionClick = async (prediction) => {
    onChange(prediction.description);
    setShowSuggestions(false);

    // Get detailed place information
    try {
      const response = await axios.get(`${API}/places/details`, {
        params: {
          place_id: prediction.place_id,
          session: sessionToken
        }
      });

      if (response.data.status === 'OK' && response.data.result) {
        const place = response.data.result;

        // Extract location name (business name or point of interest)
        let locationName = null;
        
        if (place.name && place.name !== place.formatted_address) {
          // Check if it's a business/establishment
          if (place.types?.includes('establishment') || 
              place.types?.includes('point_of_interest')) {
            locationName = place.name;
          }
        }
        
        if (onLocationNameChange && locationName) {
          onLocationNameChange(locationName);
        }

        // Extract coordinates
        if (onCoordinatesChange && place.geometry?.location) {
          const coordinates = {
            lat: place.geometry.location.lat.toString(),
            lng: place.geometry.location.lng.toString()
          };
          onCoordinatesChange(coordinates);
        }
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      toast.error('Failed to get address details');
    }
  };

  const handleClickOutside = (e) => {
    if (inputRef.current && !inputRef.current.contains(e.target)) {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative" ref={inputRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={className}
          placeholder={placeholder}
          autoComplete="off"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : error ? (
            <AlertCircle className="h-4 w-4 text-red-400" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((prediction) => (
            <button
              key={prediction.place_id}
              onClick={() => handleSuggestionClick(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-100"
            >
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {prediction.structured_formatting?.secondary_text || ''}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Status and Attribution */}
      <div className="mt-1 flex justify-between items-center">
        <div className="text-xs">
          {error ? (
            <span className="text-red-600 flex items-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              {error}
            </span>
          ) : (
            <span className="text-blue-600">
              {value && value.length >= 3 ? 
                (suggestions.length > 0 ? 
                  `${suggestions.length} address${suggestions.length === 1 ? '' : 'es'} found` :
                  loading ? 'Searching...' : 'No matches found - you can enter manually'
                ) :
                'Type 3+ characters for Australian address suggestions'
              }
            </span>
          )}
        </div>
        
        {/* Google Attribution (Required by Google Places API Terms) */}
        {suggestions.length > 0 && (
          <div className="text-xs text-gray-400">
            Powered by Google
          </div>
        )}
      </div>
    </div>
  );
};

export default GooglePlacesProxyInput;